import { AdminRole } from "@repo/api-dtos"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { RefreshCw, Upload } from "lucide-react"
import * as React from "react"
import { z } from "zod"
import { PageHeader, Section, SectionStack } from "@/components/layout"
import { BuyingGuidelines } from "@/components/postage-stamps/buying-guidelines"
import { StockSummaryCards } from "@/components/postage-stamps/stock-summary-cards"
import { UploadSheetDialog } from "@/components/postage-stamps/upload-sheet-dialog"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { useAdminRolesContext } from "@/contexts/admin-roles-context"
import { GridPager } from "@/features/admin-grid/GridPager"
import { useRegisterPaletteCommands } from "@/features/command-palette/use-register-palette-commands"
import { SheetsGrid } from "@/features/postage-stamps/SheetsGrid"
import {
	useStampSheets,
	useStampStockSummary,
} from "@/queries/marketplace/use-postage-stamps"

const SHEETS_PAGE_SIZE = 50

const searchSchema = z.object({
	page: z.number().int().min(1).optional().catch(undefined),
})

export const Route = createFileRoute("/postage-stamps/")({
	component: PostageStampsPage,
	validateSearch: (search: Record<string, unknown>) =>
		searchSchema.parse(search),
})

function PostageStampsPage() {
	const search = Route.useSearch()
	const page = search.page ?? 1
	const navigate = useNavigate()
	const { hasRole } = useAdminRolesContext()
	const canUpload = hasRole(AdminRole.MarketplaceOrderSupport)
	const summary = useStampStockSummary()
	const sheets = useStampSheets({ page, limit: SHEETS_PAGE_SIZE })
	const [uploadOpen, setUploadOpen] = React.useState(false)

	useRegisterPaletteCommands(
		"postage-stamps-upload",
		() =>
			canUpload
				? [
						{
							id: "page:postage-stamps:upload",
							label: "Upload sheet…",
							group: "page",
							icon: Upload,
							perform: (ctx) => {
								setUploadOpen(true)
								ctx.close()
							},
						},
					]
				: [],
		[canUpload],
	)

	const tiers = summary.data?.tiers ?? []
	const items = sheets.data?.items ?? []

	return (
		<div className="flex flex-col gap-6 p-6">
			<PageHeader
				title="Postage stamps"
				count={sheets.data ? `${sheets.data.total} sheets` : undefined}
				description="Prepaid La Poste stamps cut from uploaded laposte.fr sheets, used for international letters."
				actions={
					<>
						<Button
							variant="ghost"
							onClick={() => {
								void summary.refetch()
								void sheets.refetch()
							}}
							disabled={sheets.isRefetching}
						>
							<RefreshCw />
							Refresh
						</Button>
						{canUpload ? (
							<Button onClick={() => setUploadOpen(true)}>
								<Upload />
								Upload sheet
							</Button>
						) : null}
					</>
				}
			/>

			<SectionStack>
				{summary.isLoading ? (
					<Section label="Stock">
						<div className="flex flex-col gap-1">
							<Skeleton className="h-[68px] w-full" />
							<Skeleton className="h-[68px] w-full" />
						</div>
					</Section>
				) : tiers.length > 0 ? (
					<Section label="Stock">
						<StockSummaryCards tiers={tiers} canManage={canUpload} />
					</Section>
				) : null}

				<Section label="Sheets">
					{sheets.isLoading ? (
						<div className="flex flex-col gap-1">
							{Array.from({ length: 6 }, (_, index) => (
								<Skeleton key={index} className="h-9 w-full" />
							))}
						</div>
					) : sheets.isError ? (
						<p className="text-bad">
							Failed to load stamp sheets: {sheets.error.message}
						</p>
					) : items.length === 0 ? (
						<p className="text-muted-foreground">
							No stamp sheet yet. Upload the PDF of a laposte.fr stamp order to
							start the stock.
						</p>
					) : (
						<div className="flex flex-col gap-3">
							<SheetsGrid sheets={items} />
							<GridPager
								page={page}
								totalPages={sheets.data?.totalPages ?? 1}
								total={sheets.data?.total ?? items.length}
								noun="sheet"
								onPageChange={(next) =>
									navigate({
										to: "/postage-stamps",
										search: { page: next > 1 ? next : undefined },
									})
								}
							/>
						</div>
					)}
				</Section>

				<BuyingGuidelines />
			</SectionStack>

			<UploadSheetDialog open={uploadOpen} onOpenChange={setUploadOpen} />
		</div>
	)
}
